/**
 * @file Account update phone number form.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Alert from '../global/Alert';
import PhoneInput from '../global/PhoneInput';
import Button from '../global/Button';
import FormGroup from '../global/FormGroup';
import { getErrorString } from '../../util/normalize-errors';
import { PHONE_VERIFY_INTENT } from '../../constants/auth';
import { formatPhone, parsePhone } from '../../util/phone';

class AccountUpdatePhoneNumberForm extends Component {
  static propTypes = {
    account: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    accountUpdatePhoneNumber: PropTypes.func.isRequired,
    phoneVerificationRequest: PropTypes.func.isRequired,
    onAccountUpdated: PropTypes.func,
    introContent: PropTypes.node,
    verificationContent: PropTypes.func,
  };

  static defaultProps = {
    introContent: (
      <p>
        Enter your mobile number below and Waldo will text a verification code
        to the number you provide.
      </p>
    ),
    verificationContent: formattedPhone => (
      <p>Please enter the code that was sent to {formattedPhone}</p>
    ),
    onAccountUpdated: () => {},
  };

  state = {
    phone: '',
    verificationCode: '',
    phoneSubmitted: false,
    error: '',
    loading: false,
  };

  onPhoneVerificationRequest = async event => {
    event.preventDefault();

    const { phone } = this.state;
    const { phoneVerificationRequest } = this.props;

    this.setState(() => ({ error: '', loading: true }));

    try {
      await phoneVerificationRequest(
        parsePhone(phone),
        PHONE_VERIFY_INTENT.ACCOUNT_PHONE_UPDATE,
      );

      this.setState(() => ({
        loading: false,
        phoneSubmitted: true,
      }));
    } catch (error) {
      this.setState(() => ({
        loading: false,
        error: getErrorString(error),
      }));
    }
  };

  onPhoneNumberUpdate = async event => {
    event.preventDefault();

    const { verificationCode, phone } = this.state;
    const { account, accountUpdatePhoneNumber, onAccountUpdated } = this.props;

    this.setState(() => ({ error: '', loading: true }));

    try {
      await accountUpdatePhoneNumber(
        account.id,
        parsePhone(phone),
        verificationCode,
      );

      this.setState(
        () => ({
          loading: false,
        }),
        () => {
          onAccountUpdated(account.id, phone);
        },
      );
    } catch (error) {
      this.setState(() => ({
        loading: false,
        error: getErrorString(error),
      }));
    }
  };

  onPhoneInputChange = phone => {
    this.setState(() => ({ phone }));
  };

  onVerificationCodeChange = event => {
    const { value } = event.target;

    this.setState(() => ({
      verificationCode: value,
    }));
  };

  render() {
    const { phoneSubmitted, error, loading, phone, verificationCode } =
      this.state;
    const { introContent, verificationContent } = this.props;

    return (
      <div>
        {error && <Alert type="danger">{error}</Alert>}

        {!phoneSubmitted ? (
          <form method="post" onSubmit={this.onPhoneVerificationRequest}>
            {introContent}

            <PhoneInput
              id="phone"
              label="Mobile Number"
              value={phone || '+1'}
              onChange={this.onPhoneInputChange}
            />
            <div className="d-flex align-items-center justify-content-center flex-column">
              <Button type="submit" disabled={loading || !phone}>
                {loading ? 'Loading…' : 'Submit'}
              </Button>
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer noopener"
                className="mt-1 text-sm"
              >
                Terms of Use
              </a>
            </div>
          </form>
        ) : (
          <form method="post" onSubmit={this.onPhoneNumberUpdate}>
            {verificationContent(formatPhone(phone))}
            <FormGroup
              id="phone-code"
              type="text"
              label="Pin"
              name="verificationCode"
              value={verificationCode}
              onChange={this.onVerificationCodeChange}
              required
            />
            <div className="d-flex align-items-center justify-content-center flex-column">
              <Button
                btnStyle="link"
                onClick={this.onPhoneVerificationRequest}
                className="text-sm btn-pin-reset"
                disabled={loading}
              >
                Resend PIN Code
              </Button>

              <Button
                type="submit"
                disabled={loading || !verificationCode}
                className="mt-1"
              >
                {loading ? 'Loading…' : 'Update'}
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }
}

export default AccountUpdatePhoneNumberForm;
